import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { listPlans, setMyTier } from "../../api/plansApi";
import {
  FEATURES,
  featureMinTier,
  TIER_LABEL,
  TIER_PRICE_HINT,
} from "../../config/tiers";
import UpgradeModal from "./UpgradeModal";

// App-level provider holding the single upgrade modal. Any component can call
// promptUpgrade(featureKey) to open it.
const UpgradeContext = createContext({ promptUpgrade: () => {} });

export const useUpgrade = () => useContext(UpgradeContext);

export const UpgradeProvider = ({ children }) => {
  const navigate = useNavigate();
  const { refreshProfile } = useContext(AuthContext);
  const [featureKey, setFeatureKey] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const promptUpgrade = useCallback((key) => {
    if (FEATURES[key]) setFeatureKey(key);
  }, []);

  const close = useCallback(() => {
    setFeatureKey(null);
    setBusy(false);
  }, []);

  // Lazy-load the catalog the first time the modal is needed (benefit bullets).
  useEffect(() => {
    if (featureKey && plans.length === 0) {
      listPlans()
        .then(setPlans)
        .catch(() => {});
    }
  }, [featureKey, plans.length]);

  const minTier = featureKey ? featureMinTier(featureKey) : null;
  const targetPlan = plans.find((p) => p.tier_key === minTier);

  const upgradeNow = async () => {
    if (!minTier) return;
    setBusy(true);
    try {
      await setMyTier(minTier);
      await refreshProfile();
      close();
    } catch {
      setBusy(false);
    }
  };

  const seeAllPlans = () => {
    close();
    navigate("/pricing");
  };

  return (
    <UpgradeContext.Provider value={{ promptUpgrade }}>
      {children}
      {featureKey && (
        <UpgradeModal
          feature={FEATURES[featureKey]}
          tierLabel={TIER_LABEL[minTier]}
          priceHint={TIER_PRICE_HINT[minTier]}
          bullets={targetPlan?.features || []}
          busy={busy}
          onUpgrade={upgradeNow}
          onSeeAll={seeAllPlans}
          onClose={close}
        />
      )}
    </UpgradeContext.Provider>
  );
};
